/**
 * ExportCsvButton — triggers a CSV download from a server endpoint.
 *
 * Uses fetch with credentials so the session cookie comes along, then
 * builds a Blob URL and clicks an anchor to start the download. We avoid
 * `window.open(url)` because that wouldn't carry the credentials in all
 * browsers and the server's Content-Disposition response would either be
 * rendered inline (Safari) or fail silently if the cookie was lost.
 *
 * The filename comes from the response's Content-Disposition header. If
 * the server didn't send one we fall back to the `fallbackFilename` prop.
 *
 * No state library here — the button maintains its own loading/error
 * state. Callers don't need to manage anything; just pass `href`.
 */

import { useState } from 'react';
import { Download, AlertCircle } from 'lucide-react';
import { Button } from './button';

export interface ExportCsvButtonProps {
  /** Absolute or relative URL to the CSV endpoint (we'll fetch it with credentials). */
  href: string;
  /** Filename to suggest if the response didn't carry one. */
  fallbackFilename: string;
  /** Optional label override; defaults to "Export CSV". */
  label?: string;
  /** Optional variant; defaults to "tertiary" so it sits quietly next to other actions. */
  variant?: 'primary' | 'secondary' | 'tertiary';
  /** Optional size; defaults to "compact" so it fits header rows. */
  size?: 'compact' | 'default';
}

function parseFilenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  // RFC 6266 fallback: look for `filename="..."` or bare `filename=...`.
  const match = /filename\*?=(?:UTF-8''|)"?([^";]+)"?/i.exec(header);
  const captured = match?.[1];
  return captured ? decodeURIComponent(captured) : null;
}

export function ExportCsvButton({
  href,
  fallbackFilename,
  label = 'Export CSV',
  variant = 'tertiary',
  size = 'compact',
}: ExportCsvButtonProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function trigger() {
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(href, {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) {
        // Try to extract a JSON error message if the server returned one;
        // otherwise show the status text. Keeping this terse — the button
        // is small.
        let msg = `HTTP ${res.status}`;
        try {
          const body = (await res.json()) as { message?: string };
          if (body && typeof body.message === 'string') msg = body.message;
        } catch {
          /* response wasn't JSON; keep status */
        }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const filename =
        parseFilenameFromContentDisposition(res.headers.get('content-disposition')) ??
        fallbackFilename;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      // Append+click is required for Firefox; the click() without
      // appendChild is a no-op there.
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Free the Blob URL on the next tick so the download has time to start.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <span className="inline-flex items-center gap-2">
      <Button
        variant={variant}
        size={size}
        onClick={() => void trigger()}
        loading={loading}
        leftIcon={<Download className="h-3.5 w-3.5" strokeWidth={1.5} />}
      >
        {label}
      </Button>
      {error ? (
        <span
          className="inline-flex items-center gap-1 text-xs text-status-danger"
          role="alert"
          aria-live="polite"
        >
          <AlertCircle className="h-3 w-3" />
          {error}
        </span>
      ) : null}
    </span>
  );
}
